import React from 'react';
import { TFunction } from 'react-i18next';
import { object, string, array, lazy } from 'yup';
import { isDefined } from '@dxb-web-ui-framework/common/utils';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { FormActions } from '../../../DynamicComponents';
import { ValueIdentifier, ForeCastFormData } from '../../../types';
import { ChargingMethod } from '../../../../domain/ChargingMethod/entities/ChargingMethod';
import { FavouriteTransfer } from '../../../../domain/FavouriteTransfer/entities';
import {
  creditAccountNumberValueDefiner,
  chargingMethodsFormComponentsConfig,
  specialCaseFormFieldConfigs,
  constructSpecialFieldsValidations,
  constructSpecialFieldsPreloadedData,
  defaultRadioPriorityValue,
  dataToAutocompleteOptions,
  selectedCountryByCountryValueTypePredicator,
  selectedCountryPredicator,
} from '../../../../utils';
import { FileUpload } from '../../../../../../content-composer/src/components/Generic/FileUpload';
import { Country } from '../../../../../../fundTransfer/src/domain/Country/entities';
import { SwiftCode, BopCode, VocCode } from '../../../../domain/Transfer/entities';
import { TransferServiceImpl } from '../../../../domain/Transfer/usecases';
import { NO_SPECIAL_CHARS_REGEX, BIC_REGEX } from '../../constants';
import { AddBankDetailsBtn } from '../../AddBankDetailsBtn';

const externalUnknownBankTransferFormConfig = (
  t: TFunction,
  stepAData: { values: GenericObject },
  service: TransferServiceImpl,
  formData: Nullable<ForeCastFormData>,
  isLoading: boolean,
  selectedBankSwiftCode: Nullable<SwiftCode>,
  bopCodes?: BopCode[],
  vocCodes?: VocCode[],
  isLoadingBopCodes?: boolean,
  isLoadingVocCodes?: boolean,
  isLegalEntity?: boolean,
  favouriteTransfer?: Nullable<FavouriteTransfer>,
  shouldShowPriorityFields?: boolean,
  isSepaRoutingSystem?: boolean,
): FormConfig => {
  const {
    values: { routingFormData, otherMandatoryDataForUI, routingResponseData, selectedToAccount },
  } = stepAData;

  const countryAutocompleteOptions =
    dataToAutocompleteOptions((otherMandatoryDataForUI.countries as Country[]).filter(c => c.serialNumber !== 0)) || [];

  const creditAccountNumber = creditAccountNumberValueDefiner(
    routingFormData.creditAccountNumber,
    otherMandatoryDataForUI.accounts,
    otherMandatoryDataForUI.beneficiaries,
  );

  const radioComponentsConfig = chargingMethodsFormComponentsConfig(
    isLoading,
    otherMandatoryDataForUI.chargingMethods as ChargingMethod[],
    stepAData.values,
    formData,
    favouriteTransfer,
  );

  const areFieldsHidden = () => {
    if (Boolean(favouriteTransfer) && !isDefined(stepAData.values?.forecastFormData)) {
      return !favouriteTransfer;
    }
    if (!isDefined(stepAData.values?.forecastFormData)) {
      return !selectedBankSwiftCode;
    }
    return false;
  };

  const countryLogicHandler = () => {
    if (formData?.receiverCountry) {
      return selectedCountryByCountryValueTypePredicator(
        formData.receiverCountry,
        otherMandatoryDataForUI.countries as Country[],
      );
    }
    if (stepAData.values?.forecastFormData?.receiverCountry && formData?.receiverCountry === undefined) {
      return selectedCountryByCountryValueTypePredicator(
        stepAData.values.forecastFormData.receiverCountry,
        otherMandatoryDataForUI.countries as Country[],
      );
    }
    if (favouriteTransfer?.countrySn && formData?.receiverCountry === undefined) {
      return selectedCountryPredicator(
        (otherMandatoryDataForUI.countries as Country[]).find(c => c.serialNumber === favouriteTransfer.countrySn),
      );
    }

    if (selectedToAccount && selectedToAccount.prop) {
      return selectedCountryByCountryValueTypePredicator(
        selectedToAccount.prop.beneficiaryCountry,
        otherMandatoryDataForUI.countries as Country[],
      );
    }

    return { countryFound: null, isCountryAutoCompleteDisabled: false };
  };

  const { countryFound } = countryLogicHandler();

  const specialCaseFields = specialCaseFormFieldConfigs(
    t,
    routingResponseData.transferType,
    otherMandatoryDataForUI.countries,
    otherMandatoryDataForUI.currencies,
    areFieldsHidden(),
    isLoading,
    routingFormData.orderCurrency,
    stepAData.values?.selectedBankSwiftCode?.country ??
      selectedBankSwiftCode?.country ??
      favouriteTransfer?.bankCountrySn,
    routingResponseData?.sortCode ??
      stepAData.values?.forecastFormData?.receiverSortCode ??
      favouriteTransfer?.receiverSortCode,
    bopCodes,
    isLoadingBopCodes,
    vocCodes,
    isLoadingVocCodes,
  );

  if (shouldShowPriorityFields) {
    specialCaseFields.push({
      key: ValueIdentifier.PRIORITY,
      groupTitle: t('fundsTransferPage.Forms.Priority'),
      type: 'radioFormGroup',
      components: [
        {
          type: 'radio',
          label: t('fundsTransferPage.Forms.Normal'),
          key: ValueIdentifier.NORMAL,
          validateOn: 'onChange',
          disabled: isLoading,
          defaultValue: defaultRadioPriorityValue(
            stepAData.values,
            ValueIdentifier.NORMAL,
            formData,
            isSepaRoutingSystem,
            favouriteTransfer?.priorityDate,
          ),
          properties: {
            shouldGroupValidation: true,
          },
        },
        {
          type: 'radio',
          label: t('fundsTransferPage.Forms.Immediately'),
          key: ValueIdentifier.IMMEDIATE,
          validateOn: 'onChange',
          disabled: isLoading,
          defaultValue: defaultRadioPriorityValue(
            stepAData.values,
            ValueIdentifier.IMMEDIATE,
            formData,
            isSepaRoutingSystem,
            favouriteTransfer?.priorityDate,
          ),
          properties: {
            shouldGroupValidation: true,
          },
        },
      ],
      properties: {
        isRow: true,
        ml: 1,
        mt: 3,
        errorKeyToMap: ValueIdentifier.PRIORITY,
      },
    });
  }

  return {
    validationSchema: object(
      Object.assign(
        {
          [ValueIdentifier.DEBIT_ACCOUNT_NUMBER]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.CREDIT_ACCOUNT_NUMBER]: string().required(
            t('fundsTransferPage.Forms.Validation.EmptyField'),
          ),
          [ValueIdentifier.ORDER_AMOUNT]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.ORDER_CURRENCY]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          // TODO: Uncomment following lines when bug of radio group error resolved
          // [ValueIdentifier.CHARGING_METHOD]: string()
          //   .oneOf(
          //     radioComponentsConfig.map(cm => cm.key),
          //     t('fundsTransferPage.Forms.Validation.ChargingMethod'),
          //   )
          //   .required(t('fundsTransferPage.Forms.Validation.ChargingMethod')),
          [ValueIdentifier.RECEIVER_FULL_NAME]: string()
            .required(t('fundsTransferPage.Forms.Validation.BeneficiaryName'))
            .matches(NO_SPECIAL_CHARS_REGEX, t('fundsTransferPage.Forms.Validation.NoSpecialChars')),
          [ValueIdentifier.RECEIVER_ADDRESS]: string()
            .required(t('fundsTransferPage.Forms.Validation.BeneficiaryAddress'))
            .matches(NO_SPECIAL_CHARS_REGEX, t('fundsTransferPage.Forms.Validation.NoSpecialChars'))
            .min(1, t('fundsTransferPage.Forms.Validation.Min.Max.Beneficiary.Address'))
            .max(40, t('fundsTransferPage.Forms.Validation.Min.Max.Beneficiary.Address')),
          [ValueIdentifier.RECEIVER_CITY]: string()
            .required(t('fundsTransferPage.Forms.Validation.City.Beneficiary'))
            .matches(NO_SPECIAL_CHARS_REGEX, t('fundsTransferPage.Forms.Validation.NoSpecialChars'))
            .min(1, t('fundsTransferPage.Forms.Min.Max.Validation.Beneficiary.City'))
            .max(25, t('fundsTransferPage.Forms.Min.Max.Validation.Beneficiary.City')),
          [ValueIdentifier.BANK_NAME]: string().required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.RECEIVER_BIC]: string()
            .matches(BIC_REGEX, t('fundsTransferPage.Forms.Validation.Bic'))
            .required(t('fundsTransferPage.Forms.Validation.EmptyField')),
          [ValueIdentifier.RECEIVER_COUNTRY]: lazy(value => {
            switch (typeof value) {
              case 'object':
                return object().nullable(true).required(t('fundsTransferPage.Forms.GenericDrawer.Validations.Country'));
              default:
                return string().required(t('fundsTransferPage.Forms.GenericDrawer.Validations.Country'));
            }
          }),
          [ValueIdentifier.RECEIVER_COMMENTS]: string()
            .required(t('fundsTransferPage.Forms.Validation.ReceiverComments'))
            .matches(NO_SPECIAL_CHARS_REGEX, t('fundsTransferPage.Forms.Validation.NoSpecialChars'))
            .max(140, t('fundsTransferPage.Forms.Validation.MaxChars')),
          [ValueIdentifier.PRIORITY]: string()
            .oneOf(
              [ValueIdentifier.NORMAL, ValueIdentifier.IMMEDIATE],
              t('fundsTransferPage.Forms.Validation.Priority'),
            )
            .required(t('fundsTransferPage.Forms.Validation.Priority')),
          [ValueIdentifier.UPLOADED_DOCS]: array(),
        },
        constructSpecialFieldsValidations(specialCaseFields, t, isLegalEntity),
      ),
    ),
    components: [
      {
        label: t('fundsTransferPage.Forms.FromAccount'),
        key: ValueIdentifier.DEBIT_ACCOUNT_NUMBER,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.ToAccount'),
        key: ValueIdentifier.CREDIT_ACCOUNT_NUMBER,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.Amount'),
        key: ValueIdentifier.ORDER_AMOUNT,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        label: t('fundsTransferPage.Forms.Currency'),
        key: ValueIdentifier.ORDER_CURRENCY,
        type: 'text',
        disabled: true,
        defaultValue: '',
      },
      {
        key: ValueIdentifier.CHARGING_METHOD,
        type: 'radioFormGroup',
        fieldHelperText: t('fundsTransferPage.Forms.FieldHelperText.ChargingMethod'),
        groupTitle: t('fundsTransferPage.Forms.ChargingMethod'),
        components: radioComponentsConfig,
        properties: {
          isRow: true,
          ml: 1,
          mt: 3,
          errorKeyToMap: ValueIdentifier.CHARGING_METHOD,
        },
      },
      {
        label: t('fundsTransferPage.Forms.BeneficiaryName'),
        key: ValueIdentifier.RECEIVER_FULL_NAME,
        type: 'text',
        disabled: isLoading,
        defaultValue: '',
        properties: {
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
          shouldCapitalizeValue: true,
        },
      },
      {
        label: t('fundsTransferPage.Forms.BeneficiaryAddress'),
        key: ValueIdentifier.RECEIVER_ADDRESS,
        type: 'text',
        disabled: isLoading,
        defaultValue: '',
        properties: {
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
          shouldCapitalizeValue: true,
        },
      },
      {
        label: t('fundsTransferPage.Forms.BeneficiaryCity'),
        key: ValueIdentifier.RECEIVER_CITY,
        type: 'text',
        disabled: isLoading,
        defaultValue: '',
        properties: {
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
          shouldCapitalizeValue: true,
        },
      },
      {
        label: t('fundsTransferPage.Forms.BeneficiaryCountry'),
        key: ValueIdentifier.RECEIVER_COUNTRY,
        type: 'autocomplete',
        options: countryAutocompleteOptions,
        defaultValue: countryFound,
        disabled: isLoading,
        properties: {
          forcePopupIcon: true,
          mt: 3,
          noOptionsText: t('fundsTransferPage.Forms.AddBankDetails.NoOptions.Message'),
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
          shouldCapitalizeValue: true,
        },
      },
      {
        label: t('fundsTransferPage.Forms.BankName'),
        key: ValueIdentifier.BANK_NAME,
        type: 'text',
        disabled: true,
        defaultValue: '',
        properties: {
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
        },
      },
      {
        label: t('fundsTransferPage.Forms.BankBic'),
        key: ValueIdentifier.RECEIVER_BIC,
        type: 'text',
        disabled: true,
        defaultValue: '',
        properties: {
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
        },
      },
      {
        label: t('fundsTransferPage.Forms.Comments'),
        key: ValueIdentifier.RECEIVER_COMMENTS,
        type: 'text',
        disabled: isLoading,
        defaultValue: '',
        properties: {
          customLogic: {
            hidden: () => areFieldsHidden(),
          },
          shouldCapitalizeValue: true,
        },
      },
      ...specialCaseFields,
      {
        key: ValueIdentifier.BANK_DETAILS,
        type: 'dynamicComponent',
        properties: {
          component: (props: any) => (
            <AddBankDetailsBtn
              formBuilderProps={props}
              t={t}
              otherMandatoryDataForUI={otherMandatoryDataForUI}
              areFieldsHidden={areFieldsHidden()}
            />
          ),
        },
      },
      {
        type: 'dynamicComponent',
        key: ValueIdentifier.UPLOADED_DOCS,
        properties: {
          component: (props: any) => (
            <FileUpload
              boxProps={{ mt: 3 }}
              description={t('fundsTransferPage.Forms.FileUploader.Description')}
              label={t('fundsTransferPage.Forms.FileUploader.Label')}
              type={t('fundsTransferPage.Forms.FileUploader.FileUploader.Type')}
              service={service}
              transactionId={routingResponseData.appTransactionId}
              shouldUploadDocuments={true}
              preselectedFileItems={
                formData?.uploadedDocs ? formData.uploadedDocs : stepAData.values?.forecastFormData?.uploadedDocs
              }
              formBuilderProps={props}
              isLoading={props.externalProps.isLoading}
              externalFilesSetter={props.externalEffectCallbacks.setFormData}
              isFileItemAction={true}
            >
              <FormActions
                onClickCancel={props.externalEffectCallbacks?.onClickCancel}
                isLoading={props.externalProps.isLoading}
              />
            </FileUpload>
          ),
        },
      },
    ],
    preloadedData: Object.assign(
      {
        [ValueIdentifier.DEBIT_ACCOUNT_NUMBER]: otherMandatoryDataForUI.fromAccount.iban,
        [ValueIdentifier.CREDIT_ACCOUNT_NUMBER]: creditAccountNumber,
        [ValueIdentifier.ORDER_AMOUNT]: routingFormData.orderAmount,
        [ValueIdentifier.ORDER_CURRENCY]: otherMandatoryDataForUI.currency.shortDescription?.toUpperCase(),
        [ValueIdentifier.CHARGING_METHOD]: formData?.chargingMethod
          ? formData.chargingMethod
          : stepAData.values?.forecastFormData?.chargingMethod
          ? stepAData.values.forecastFormData.chargingMethod
          : favouriteTransfer?.chargingMethod
          ? String(favouriteTransfer.chargingMethod)
          : '3',
        [ValueIdentifier.RECEIVER_FULL_NAME]: formData?.receiverFullName
          ? formData.receiverFullName?.toUpperCase()
          : stepAData.values?.forecastFormData?.receiverFullName
          ? stepAData.values.forecastFormData.receiverFullName?.toUpperCase()
          : favouriteTransfer?.receiverFullName
          ? favouriteTransfer.receiverFullName?.toUpperCase()
          : selectedToAccount?.prop?.creditorFullName
          ? selectedToAccount?.prop?.creditorFullName?.toUpperCase()
          : '',
        [ValueIdentifier.RECEIVER_ADDRESS]: formData?.receiverAddress
          ? formData.receiverAddress?.toUpperCase()
          : stepAData.values?.forecastFormData?.receiverAddress
          ? stepAData.values.forecastFormData.receiverAddress?.toUpperCase()
          : favouriteTransfer?.receiverAddress
          ? favouriteTransfer.receiverAddress?.toUpperCase()
          : selectedToAccount?.prop?.beneficiaryAddress
          ? selectedToAccount?.prop?.beneficiaryAddress?.toUpperCase()
          : '',
        [ValueIdentifier.RECEIVER_CITY]: formData?.receiverCity
          ? formData.receiverCity?.toUpperCase()
          : stepAData.values?.forecastFormData?.receiverCity
          ? stepAData.values.forecastFormData.receiverCity?.toUpperCase()
          : favouriteTransfer?.receiverCity
          ? favouriteTransfer.receiverCity?.toUpperCase()
          : selectedToAccount?.prop?.beneficiaryCity
          ? selectedToAccount?.prop?.beneficiaryCity?.toUpperCase()
          : '',
        [ValueIdentifier.BANK_NAME]: selectedBankSwiftCode?.bankDescr
          ? selectedBankSwiftCode.bankDescr?.toUpperCase()
          : formData?.bankName
          ? formData.bankName?.toUpperCase()
          : stepAData.values?.forecastFormData?.bankName
          ? stepAData.values.forecastFormData.bankName?.toUpperCase()
          : favouriteTransfer?.bankName
          ? favouriteTransfer.bankName?.toUpperCase()
          : '',
        [ValueIdentifier.RECEIVER_BIC]: selectedBankSwiftCode?.bic
          ? selectedBankSwiftCode.bic?.toUpperCase()
          : formData?.receiverBic
          ? formData.receiverBic?.toUpperCase()
          : stepAData.values?.forecastFormData?.receiverBic
          ? stepAData.values.forecastFormData.receiverBic?.toUpperCase()
          : favouriteTransfer?.receiverBic
          ? favouriteTransfer.receiverBic?.toUpperCase()
          : '',
        [ValueIdentifier.RECEIVER_COUNTRY]: countryFound,
        [ValueIdentifier.RECEIVER_COMMENTS]: formData?.receiverComments
          ? formData.receiverComments?.toUpperCase()
          : stepAData.values?.forecastFormData?.receiverComments
          ? stepAData.values.forecastFormData.receiverComments?.toUpperCase()
          : favouriteTransfer?.receiverComments
          ? favouriteTransfer.receiverComments?.toUpperCase()
          : '',
        [ValueIdentifier.PRIORITY]: stepAData.values?.forecastFormData?.priority
          ? stepAData.values?.forecastFormData?.priority
          : isSepaRoutingSystem
          ? ValueIdentifier.IMMEDIATE
          : ValueIdentifier.NORMAL,
        [ValueIdentifier.UPLOADED_DOCS]: formData?.uploadedDocs
          ? formData.uploadedDocs
          : stepAData.values?.forecastFormData?.uploadedDocs || [],
      },
      constructSpecialFieldsPreloadedData(specialCaseFields, {
        formData,
        routingResponseData,
        forecastFormData: stepAData.values?.forecastFormData,
        favouriteTransfer,
        bopCodes,
        vocCodes,
      }),
    ),
    preloadedDataValidationOptions: { shouldValidate: false, shouldDirty: true },
  };
};

export { externalUnknownBankTransferFormConfig };
